import { useEffect, useRef, useState } from 'react';

// Event name that any MFE may dispatch on `window` to tell the Shell its
// above-the-fold content has settled. Optional by design — the hook's timeout
// fallback covers MFEs that don't participate.
export const SHELL_CONTENT_SETTLED_EVENT = 'shell:content-settled' as const;

export type UseContentSettledOptions = {
  enabled: boolean; // false bypasses gating entirely and returns true synchronously.
  timeoutMs: number; // Hard fallback so a missing dispatch can't block paint forever.
};

export const useContentSettled = ({ enabled, timeoutMs }: UseContentSettledOptions): boolean => {
  const [settled, setSettled] = useState(!enabled);
  const prevEnabled = useRef(enabled);

  // BasePage stays mounted across client-side navigation, so re-gate when
  // `enabled` flips (e.g. nav from a non-gated route into an offer route).
  // Without this the mount-time initial value would be stale and the Footer
  // would mount ungated. Adjusting state during render keeps the first paint
  // after the flip already gated.
  if (prevEnabled.current !== enabled) {
    prevEnabled.current = enabled;
    setSettled(!enabled);
  }

  useEffect(() => {
    if (!enabled) return undefined;
    const onSettled = () => setSettled(true);
    window.addEventListener(SHELL_CONTENT_SETTLED_EVENT, onSettled);
    const timer = window.setTimeout(() => setSettled(true), timeoutMs);
    return () => {
      window.removeEventListener(SHELL_CONTENT_SETTLED_EVENT, onSettled);
      window.clearTimeout(timer);
    };
  }, [enabled, timeoutMs]);

  return settled;
};
